import { useState } from "react";
import { useNavigate } from "react-router";
import { authedFetch } from "./api-client";
import type { ApiEnvelope } from "@a2/core/schemas";
import type { SchrijvenTaskType } from "@a2/core";
import type { SessionMode } from "../components/welcome/PrepProgress";

type StartResponse = {
  sessionId: string;
  mode: SessionMode;
  parts: number[];
  totalTurns: number;
  partialMediaFailure?: boolean;
  questions: unknown;
};

export type UseStartSpreken = {
  /** POST /exam/start for the given mode, then navigate to the runner. */
  start: (mode: SessionMode) => Promise<void>;
  pending: boolean;
  /** Flips true the moment /exam/start resolves, so PrepProgress can snap to 100%. */
  done: boolean;
  error: string | null;
};

/**
 * Extracted verbatim from ModePicker's start() — the /exam/start POST +
 * navigate-with-state handoff. Lifted to a hook so the shared BeginBar can
 * fire it while SprekenModeBody stays presentational. API contract unchanged.
 */
export function useStartSpreken(errorMessage: string): UseStartSpreken {
  const nav = useNavigate();
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(mode: SessionMode) {
    setError(null);
    setDone(false);
    setPending(true);
    let json: ApiEnvelope<StartResponse>;
    try {
      const res = await authedFetch("/exam/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ mode }),
      });
      json = (await res.json()) as ApiEnvelope<StartResponse>;
    } catch {
      setError(errorMessage);
      setPending(false);
      return;
    }
    if (!json.ok) {
      setError(json.error.message);
      setPending(false);
      return;
    }
    // Snap the bar to 100% briefly so the user sees the visual finish before
    // the route transition. 250 ms ≈ one progress-bar transition duration.
    setDone(true);
    const { sessionId } = json.data;
    setTimeout(() => {
      // The runner has no GET-by-id endpoint to rehydrate on a direct URL hit,
      // so the start-response is the bootstrap input it reads from location state.
      nav(`/exam/${sessionId}`, { state: { startResponse: json.ok ? json.data : null } });
    }, 250);
  }

  return { start, pending, done, error };
}

type CreateSessionResponse = {
  ok: true;
  data: {
    sessionId: string;
    examId: string;
    variantIds: Partial<Record<SchrijvenTaskType, string>>;
  };
};

/** "exam" runs the full four-task session; a SchrijvenTaskType runs one task. */
export type SchrijvenMode = "exam" | SchrijvenTaskType;

export type UseStartSchrijven = {
  start: (mode: SchrijvenMode) => Promise<void>;
  pending: boolean;
  error: string | null;
};

/**
 * Replicated from routes/schrijven/home.tsx — POST /schrijven/sessions then a
 * direct navigate (no PrepProgress). API contract unchanged.
 */
export function useStartSchrijven(networkErrorMessage: string, unknownErrorMessage: string): UseStartSchrijven {
  const nav = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(mode: SchrijvenMode) {
    setPending(true);
    setError(null);
    const body = mode === "exam" ? { mode: "exam" } : { mode: "practice", taskType: mode };
    try {
      const res = await authedFetch("/schrijven/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as CreateSessionResponse | { ok: false; error: { message: string } };
      if (!json.ok) {
        setError(json.ok === false ? json.error.message : unknownErrorMessage);
        setPending(false);
        return;
      }
      const state = { sessionId: json.data.sessionId, variantIds: json.data.variantIds };
      if (mode === "exam") {
        await nav("/schrijven/exam", { state });
      } else {
        await nav(`/schrijven/practice/${mode}`, { state });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : networkErrorMessage);
      setPending(false);
    }
  }

  return { start, pending, error };
}
